import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import ToolFavoriteHeart from '@/components/Tools/ToolFavoriteHeart';
import { OUTPUT_TYPE_ICON, outputTypeLabel } from '@/components/Tools/ToolCard';
import { formatCount } from '@/utils/agent_catalog';

const isWeb = Platform.OS === 'web';
const MIN_USAGE_TO_SHOW = 25;
const SIDE_CARD_COUNT = 4;
// Below this measured width the lead card can't sit beside a 2×2 of side
// cards without crushing them, so the section stacks. Measured (not window)
// width matters because the page can render inside a narrow column next to
// an open side panel.
const SIDE_BY_SIDE_MIN_WIDTH = 900;

const usageOf = (tool) => Number(tool?.stats?.usageCount) || 0;

// ─── Lead card ──────────────────────────────────────────────────────────────

function LeadCard({ tool, onPress, colors, isXS, isSM, palette }) {
  const [hovered, setHovered] = useState(false);
  const usage = usageOf(tool);
  const avatarSize = 48;
  const baseBg = palette.surface;

  return (
    <Pressable
      onPress={() => onPress?.(tool)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
      style={{
        flex: isSM ? undefined : 0.95,
        width: isSM ? '100%' : undefined,
        minHeight: isXS ? 220 : isSM ? 220 : 280,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: hovered ? palette.activeBorder : palette.border,
        backgroundColor: hovered ? palette.hover : baseBg,
        overflow: 'hidden',
        ...(isWeb && {
          cursor: 'pointer',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        }),
      }}
    >
      <View style={{ flex: 1, padding: isXS ? 18 : 22, justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <View style={{ borderRadius: 18, overflow: 'hidden' }}>
            <ToolAvatar
              tool={tool}
              size={avatarSize}
              iconSize={Math.round(avatarSize * 0.46)}
              borderRadius={20}
              backgroundColor={palette.preview}
              color={palette.icon}
            />
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {usage >= MIN_USAGE_TO_SHOW ? (
              <View
                style={{
                  flexDirection: 'row', alignItems: 'center', gap: 6,
                  height: 26, paddingHorizontal: 10, borderRadius: 999,
                  backgroundColor: palette.preview,
                }}
              >
                <Icon name="bolt" size={10} color={palette.icon} solid />
                <Text size="xs" weight="bold" style={{ color: palette.icon, fontVariant: ['tabular-nums'] }}>
                  {formatCount(usage)} runs
                </Text>
              </View>
            ) : null}
            <ToolFavoriteHeart tool={tool} />
          </View>
        </View>

        <View style={{ marginTop: 18, maxWidth: 520 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {tool?.category?.name ? (
              <Text size="xxs" weight="bold" style={{ color: palette.icon }}>
                {tool.category.name}
              </Text>
            ) : null}
            {tool?.category?.name && tool?.outputType ? (
              <View style={{ width: 3, height: 3, borderRadius: 2, backgroundColor: colors.text_color_tertiary }} />
            ) : null}
            {tool?.outputType ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Icon name={OUTPUT_TYPE_ICON[tool.outputType] || 'circle'} size={10} color={colors.text_color_tertiary} />
                <Text size="xxs" weight="bold" color="tertiary" >
                  {outputTypeLabel(tool.outputType)}
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            tag="h3"
            weight="semibold"
            numberOfLines={2}
            style={{
              fontSize: 22,
              lineHeight: 28,
              letterSpacing: -0.8,
              // A global stylesheet centers `h3` on web; this is a left-aligned card title.
              textAlign: 'left',
              color: colors.text_color_primary,
            }}
          >
            {tool?.name}
          </Text>
          <Text
            color="secondary"
            numberOfLines={2}
            style={{ marginTop: 8, fontSize: 14, lineHeight: 21 }}
          >
            {tool?.shortDescription || tool?.description}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: isXS ? 14 : 20 }}>
            <View
              style={{
                flexDirection: 'row', alignItems: 'center', gap: 8,
                height: 38, paddingHorizontal: 16, borderRadius: 10,
                backgroundColor: palette.ink,
                ...(isWeb && { transition: 'background-color 0.2s ease' }),
              }}
            >
              <Text size="s" weight="bold" style={{ color: palette.onInk }}>Open tool</Text>
              <Icon name="arrow-right" size={12} color={palette.onInk} />
            </View>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

// ─── Side card ──────────────────────────────────────────────────────────────

function SideCard({ tool, onPress, colors, isXS, palette, fixedWidth = null }) {
  const [hovered, setHovered] = useState(false);
  const usage = usageOf(tool);
  const avatarSize = 40;

  return (
    <Pressable
      onPress={() => onPress?.(tool)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={tool?.name}
      style={{
        ...(fixedWidth ? { width: fixedWidth } : { flex: 1, minWidth: 0 }),
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        padding: isXS ? 14 : 16,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: hovered ? palette.activeBorder : palette.border,
        backgroundColor: hovered ? palette.hover : palette.surface,
        ...(isWeb && {
          cursor: 'pointer',
          transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
        }),
      }}
    >
      <View style={{ borderRadius: 13, overflow: 'hidden', flexShrink: 0 }}>
        <ToolAvatar
          tool={tool}
          size={avatarSize}
          iconSize={Math.round(avatarSize * 0.46)}
          borderRadius={13}
          backgroundColor={palette.preview}
          color={palette.icon}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
          {tool?.category?.name ? (
            <Text size="xxs" weight="bold" numberOfLines={1} style={{ color: palette.icon }}>
              {tool.category.name}
            </Text>
          ) : <View />}
          {usage >= MIN_USAGE_TO_SHOW ? (
            <Text size="xxs" color="tertiary" style={{ fontVariant: ['tabular-nums'] }}>
              {formatCount(usage)} runs
            </Text>
          ) : null}
        </View>
        <Text weight="semibold" size="m" numberOfLines={2} style={{ letterSpacing: -0.2 }}>
          {tool?.name}
        </Text>
        <Text size="s" color="secondary" numberOfLines={2} style={{ marginTop: 3, lineHeight: 18 }}>
          {tool?.shortDescription || tool?.description}
        </Text>
      </View>
    </Pressable>
  );
}

// ─── Section ────────────────────────────────────────────────────────────────

/**
 * Featured tools: the most-used tool gets a compact lead card,
 * the next four sit beside it in a 2×2. On phones the lead stacks on top and
 * the side cards become a horizontal strip.
 */
function ToolsSpotlight({ tools, onToolPress, title = 'Featured', subtitle = null, style }) {
  const { colors, theme } = useTheme();
  const { isXS, isSM: isSMWindow, windowWidth } = useResponsiveness();
  const palette = getCatalogPalette(theme);
  const [measuredWidth, setMeasuredWidth] = useState(null);

  const onLayout = useCallback((e) => {
    const w = e?.nativeEvent?.layout?.width;
    if (typeof w === 'number' && w > 0) setMeasuredWidth(Math.round(w));
  }, []);

  const { lead, side } = useMemo(() => {
    const list = Array.isArray(tools) ? tools.filter(Boolean) : [];
    if (!list.length) return { lead: null, side: [] };
    const sorted = [...list].sort((a, b) => usageOf(b) - usageOf(a));
    return { lead: sorted[0], side: sorted.slice(1, 1 + SIDE_CARD_COUNT) };
  }, [tools]);

  if (!lead) return null;

  // "Stacked" = lead on top, side cards below. Window breakpoint handles
  // tablets; the measured width handles a wide window with a narrow column.
  const isSM = isSMWindow || (measuredWidth != null && measuredWidth < SIDE_BY_SIDE_MIN_WIDTH);
  const gap = isXS ? 10 : 14;
  const sideCardWidth = isXS ? Math.min(300, Math.max(240, windowWidth - 70)) : null;

  const renderSide = () => {
    if (!side.length) return null;
    if (isXS) {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ marginHorizontal: -15, marginTop: gap }}
          contentContainerStyle={{ flexDirection: 'row', gap, paddingHorizontal: 15 }}
        >
          {side.map((tool) => (
            <SideCard
              key={tool.id || tool._id || tool.slug}
              tool={tool}
              onPress={onToolPress}
              colors={colors}
              isXS={isXS}
              palette={palette}
              fixedWidth={sideCardWidth}
            />
          ))}
        </ScrollView>
      );
    }
    const rows = [side.slice(0, 2), side.slice(2, 4)].filter((r) => r.length);
    return (
      <View style={{ flex: isSM ? undefined : 1.4, width: isSM ? '100%' : undefined, gap, marginTop: isSM ? gap : 0 }}>
        {rows.map((row, i) => (
          <View key={i} style={{ flexDirection: 'row', gap, flex: isSM ? undefined : 1 }}>
            {row.map((tool) => (
              <SideCard
                key={tool.id || tool._id || tool.slug}
                tool={tool}
                onPress={onToolPress}
                colors={colors}
                isXS={isXS}
                palette={palette}
              />
            ))}
            {row.length === 1 ? <View style={{ flex: 1 }} /> : null}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={[{ width: '100%' }, style]} onLayout={onLayout}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <Text tag="h2" weight="semibold" style={{ fontSize: isXS ? 18 : 20, letterSpacing: -0.4 }}>
          {title}
        </Text>
        {subtitle ? <Text size="s" color="tertiary">{subtitle}</Text> : null}
      </View>
      <View style={{ flexDirection: isSM ? 'column' : 'row', gap: isSM ? 0 : gap, alignItems: 'stretch' }}>
        <LeadCard
          tool={lead}
          onPress={onToolPress}
          colors={colors}
          isXS={isXS}
          isSM={isSM}
          palette={palette}
        />
        {renderSide()}
      </View>
    </View>
  );
}

export default React.memo(ToolsSpotlight);
